import { http } from '@/api/http';
import { Preferences } from '@capacitor/preferences';
import { Device } from '@capacitor/device';

export interface NotificationEvent {
  key: string;
  label: string;
  description?: string;
}

interface Env {
  status: string;
  result: unknown;
}

// Fallback list used when the API doesn't return events
const FALLBACK_EVENTS: NotificationEvent[] = [
  { key: 'new_order',       label: 'New Order',             description: 'When a new order is placed' },
  { key: 'order_changed',   label: 'Order Status Changed',  description: 'When an order status is updated' },
  { key: 'payment_received',label: 'Payment Received',      description: 'When a payment is processed' },
  { key: 'daily_digest',    label: 'Daily Digest',          description: 'Daily summary of store activity' },
  { key: 'waiver_sharing',  label: 'Waiver Sharing',        description: 'When a customer shares a waiver' },
];

interface ApiEventItem {
  label?: string;
  event?: string;
  [key: string]: unknown;
}

export async function getNotificationEvents(): Promise<NotificationEvent[]> {
  try {
    const raw = await http.get<Env>('/default-config/notification_events');
    if (raw.status !== 'OK') return FALLBACK_EVENTS;

    // Shape: { status, result: { data: [ { label, event, ... } ] } }
    const result = raw.result as Record<string, unknown> | null;
    const data = result && Array.isArray((result as Record<string, unknown>)['data'])
      ? ((result as Record<string, unknown>)['data'] as ApiEventItem[])
      : Array.isArray(result)
        ? (result as ApiEventItem[])
        : null;

    if (data && data.length) {
      return data.flatMap(e => {
        const key   = typeof e.event === 'string' ? e.event : null;
        const label = typeof e.label === 'string' ? e.label : null;
        if (!key || !label) return [];
        return [{ key, label }];
      });
    }
  } catch {
    // fall through to fallback
  }
  return FALLBACK_EVENTS;
}

// ── Local preference store ────────────────────────────────────────────────────

const PREFS_KEY = 'notif_push_prefs';

export function loadPushPrefs(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function savePushPrefs(prefs: Record<string, boolean>): void {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export async function loadPushPrefsFromApi(): Promise<Record<string, boolean> | null> {
  const raw = localStorage.getItem('auth_user');
  if (!raw) return null;
  const u = JSON.parse(raw) as { user_id?: number; id?: number };
  const userId = u.user_id ?? u.id;
  if (!userId) return null;

  try {
    const res = await http.get<{
      status: string;
      result: { data: { options?: { push_notification_settings?: { notification_list?: Array<Record<string, boolean>> } } } };
    }>(`/users/${userId}/info`);

    if (res.status !== 'OK') return null;
    const list = res.result?.data?.options?.push_notification_settings?.notification_list;
    if (!Array.isArray(list)) return null;
    return Object.assign({}, ...list) as Record<string, boolean>;
  } catch {
    return null;
  }
}

export async function syncPushPrefsToApi(prefs: Record<string, boolean>): Promise<void> {
  const raw = localStorage.getItem('auth_user');
  if (!raw) return;
  const u = JSON.parse(raw) as { user_id?: number; id?: number };
  const userId = u.user_id ?? u.id;
  if (!userId) return;

  const notification_list = Object.entries(prefs).map(([key, value]) => ({ [key]: value }));
  const enable_push_notification = Object.values(prefs).some(v => v === true);
  await http.post(`/users/${userId}/push-notification`, {
    enable_push_notification,
    notification_list,
  });
}

export function isPushEnabled(key: string): boolean {
  const prefs = loadPushPrefs();
  return prefs[key] !== false;
}

// ── Device token registration ─────────────────────────────────────────────────

const DEVICE_ID_KEY = 'device_id';

async function getOrCreateDeviceId(): Promise<string> {
  try {
    const { identifier } = await Device.getId();
    if (identifier) return identifier;
  } catch { /* fall through */ }
  const { value } = await Preferences.get({ key: DEVICE_ID_KEY });
  if (value) return value;
  const id = crypto.randomUUID();
  await Preferences.set({ key: DEVICE_ID_KEY, value: id });
  return id;
}

function getDeviceName(): string {
  const ua = navigator.userAgent;
  const match = ua.match(/Android[\s\d.]+;\s*([^)]+?)\s*(Build\/|[)])/);
  if (match?.[1]) return match[1].trim();
  if (/android/i.test(ua)) return 'Android Device';
  if (/iphone/i.test(ua)) return 'iPhone';
  if (/ipad/i.test(ua))   return 'iPad';
  return 'Unknown Device';
}

export async function registerDeviceToken(fcmToken: string, force = false): Promise<void> {
  if (!localStorage.getItem('auth_token')) { console.log('[FCM] skip: not logged in'); return; }
  const raw = localStorage.getItem('auth_user');
  if (!raw) { console.log('[FCM] skip: no auth_user'); return; }
  const u = JSON.parse(raw) as { user_id?: number; id?: number; store_id?: number; location_id?: number };
  const userId  = u.user_id ?? u.id;
  const storeId = u.store_id ?? u.location_id ?? 0;
  if (!userId) { console.log('[FCM] skip: no userId'); return; }

  if (!force) {
    const { value: savedToken } = await Preferences.get({ key: 'fcm_token' });
    if (savedToken === fcmToken) { console.log('[FCM] skip: token unchanged'); return; }
  }

  console.log('[FCM] registering token for userId:', userId, force ? '(forced)' : '');
  await http.post('/users/device-token', {
    token:     fcmToken,
    device:    getDeviceName(),
    user_id:   userId,
    device_id: await getOrCreateDeviceId(),
    store_id:  storeId,
  });
  await Preferences.set({ key: 'fcm_token', value: fcmToken });
  console.log('[FCM] token saved OK');
}
